import { defineComponent, inject, onMounted, ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from '@/plugins/vuex';
import StemdoerService from '@/sections/admin/entities/stemdoer/stemdoer.service';
import { IStemdoer, Stemdoers } from '@/shared/model/stemdoer.model';
import { IRate, Rate, Rates } from '@/shared/model/rate.model';
import StemdoerList from './stemdoer-list/StemdoerList.vue';
import TopFilter from './top-filter/TopFilter.vue';
import BtnMyTeam from './btn-my-team/BtnMyTeam.vue';
import BtnEditRates from './btn-edit-rates/BtnEditRates.vue';
import NoResultInfo from './no-result-info/NoResultInfo.vue';
import Filters, { FilterFields } from './model/Filters';
import SwitchAvailability from './top-filter/filter/switch-availability/SwitchAvailability.vue';
import { filterStemdoers, filterStemdoersClosestMatch } from './model/StemdoerFilter';
import StemdoerListSkeleton from './stemdoer-list-skeleton/StemdoerListSkeleton.vue';
import { IAtocompleteProps } from './model/Filter';
import EmptyStemdoerList from './empty-stemdoer-list/EmptyStemdoerList.vue';
import TeamRequestService from '@/sections/admin/entities/team-request/team-request.service';
import ClientService from '@/sections/admin/entities/client/client.service';
import CompanyService from '@/sections/admin/entities/company/company.service';

export default defineComponent({
  props: {
    teamRequestId: {
      type: String,
      required: false
    },
    loadTrToCart: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    TopFilter,
    StemdoerList,
    SwitchAvailability,
    BtnMyTeam,
    BtnEditRates,
    NoResultInfo,
    StemdoerListSkeleton,
    EmptyStemdoerList
  },
  setup(props) {
    const store = useStore();
    const stemdoerService: StemdoerService = inject('stemdoerService');
    const teamRequestService: TeamRequestService = inject('teamRequestService');
    const clientService = inject('clientService') as () => ClientService;
    const companyService = inject('companyService') as CompanyService;

    const stemdoers = ref<Stemdoers>(Stemdoers.empty());
    const stemdoersNotInCart = ref<Stemdoers>(Stemdoers.empty());
    const visibleStemdoers = ref<Stemdoers>(Stemdoers.empty());
    const rates = ref<Rates>(Rates.empty());
    const isLoading = ref(true);
    const isFiltered = ref(false);
    const isFilteredByClosestMatch = ref(false);
    const isManager = computed(() => store.getters.account?.authorities.includes('ROLE_GESTOR') ?? false);
    const isUserClient = computed(() => store.getters.account?.authorities.includes('ROLE_CLIENT') ?? false);
    const defaultClientSelector = computed(() => {
      const client = store.getters['cartStore/clientSelected'];
      return props.teamRequestId ? client : null;
    });
    const defaultCompanySelector = computed(() => {
      const company = store.getters['cartStore/companySelected'];
      return props.teamRequestId ? company : null;
    });
    const cartTotalItems = computed(() => {
      return store.getters['cartStore/cartTotalItems'];
    });
    const displaySkeleton = computed(() => {
      return isLoading.value || (stemdoers.value.length === 0 && isLoading.value);
    });
    const isStemdoersListEmpty = computed(() => {
      if (visibleStemdoers.value.length == 0) {
        return true;
      }
      return false;
    });

    const isScrolled = ref(false);
    const isWindowScrolled = ref(false);

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 0 ? true : false;
      isWindowScrolled.value = window.scrollY > 45 ? true : false;
      const hasOpenAutocomplete = document.querySelector('.v-menu__content:not([style*="display: none"])');
      if (hasOpenAutocomplete) {
        closeAllInputs();
      }
    };

    const closeAllInputs = () => {
      document.querySelectorAll('.v-autocomplete').forEach(el => {
        const vueInstance = (el as any).__vue__;
        if (vueInstance?.isMenuActive) {
          vueInstance.isMenuActive = false;
        }
      });
    };

    onMounted(async () => {
      if (!isManager.value) {
        rates.value = await companyService.retrieveCompanyRates();
      }
      retrieveAllStemdoers();
      if (props.loadTrToCart === true && props.teamRequestId) loadTeamRequestInfo(props.teamRequestId);
      if (isUserClient.value) {
        const clientId = store.getters['clientAccount'].id;
        clientService()
          .find(clientId)
          .then(res => {
            store.commit('cartStore/setClient', res);
          });
      }
      window.addEventListener('scroll', handleScroll);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    watch(
      () => store.getters['filterStore/filters'],
      (filters: Filters, oldFilters: Filters) => {
        if (hasTechFilterChanged(filters, oldFilters)) {
          retrieveAllStemdoers();
        } else {
          visibleStemdoers.value = filter(filters, stemdoersNotInCart.value);
        }
      },
      { deep: true }
    );

    const hasTechFilterChanged = (newFiltersState: Filters, oldFiltersState: Filters) => {
      const oldTechFilter = oldFiltersState.getFiltersByField(FilterFields.Technology)[0];
      const newTechFilter = newFiltersState.getFiltersByField(FilterFields.Technology)[0];
      const oldTechFilterProps = oldTechFilter?.props as IAtocompleteProps;
      const newTechFilterProps = newTechFilter?.props as IAtocompleteProps;
      return !oldTechFilter || !newTechFilter || oldTechFilterProps.value.id !== newTechFilterProps.value.id;
    };

    const loadTeamRequestInfo = (teamRequestId: string) => {
      store.commit('cartStore/clearCart');
      teamRequestService.find(teamRequestId).then(tr => {
        store.commit('cartStore/setClient', tr.client);
        tr.stemdoerRates.forEach(sr => {
          if (sr.stemdoer.availability.available == true) {
            store.commit('cartStore/addStemdoer', sr.stemdoer);
          }
        });
      });
    };

    const retrieveAllStemdoers = async () => {
      isFiltered.value = false;
      isLoading.value = true;
      const verticalId = ref<string | null>(null);

      const filters = store.getters['filterStore/filters'];
      const techFilter = filters.find(filter => filter.field == FilterFields.Technology);
      const clientId = store.getters['cartStore/clientSelected']?.id;
      const date = new Date();

      try {
        let allStemdoers: any[] = [];
        let verticalParam = 'all';
        if (techFilter && techFilter.props && techFilter.props.value && techFilter.props.value.id) {
          verticalId.value = techFilter.props.value.id;
          verticalParam = verticalId.value;
        }
        const res = await stemdoerService.retrieveStemdoersByDate(date, verticalParam, clientId);
        allStemdoers = res.data.content ? res.data.content : res.data;
        stemdoers.value = Stemdoers.from(allStemdoers);
        stemdoers.value.setRates(rates.value);
        stemdoersNotInCart.value = getStemdoersNotInCart(stemdoers.value);
        visibleStemdoers.value = filter(filters, stemdoersNotInCart.value);
        isLoading.value = false;
      } catch (err) {
        console.error(err);
        isLoading.value = false;
      }
    };

    const getStemdoersNotInCart = (stemdoers: Stemdoers): Stemdoers => {
      let stemdoersNotInCart = Stemdoers.from([...stemdoers]);
      if (cartTotalItems.value > 0) {
        const cartItemsIds = store.getters['cartStore/cartItems'].map(s => s.id);
        stemdoersNotInCart = stemdoersNotInCart.getFilterByIds(cartItemsIds);
      }
      return stemdoersNotInCart;
    };

    const filter = (filters: Filters, stemdoers: Stemdoers): Stemdoers => {
      let filteredStemdoers = [...stemdoers];
      filteredStemdoers = Stemdoers.fromI(filterStemdoers(filters, filteredStemdoers));
      isFiltered.value = true;
      isFilteredByClosestMatch.value = false;
      if (filteredStemdoers.length === 0) {
        filteredStemdoers = Stemdoers.fromI(filterByClosestMatch(filters, stemdoersNotInCart.value));
      }
      return Stemdoers.from(filteredStemdoers);
    };

    const filterByClosestMatch = (filters: Filters, stemdoers: IStemdoer[]): IStemdoer[] => {
      let filteredStemdoers: IStemdoer[] = [...stemdoers];
      filteredStemdoers = filterStemdoersClosestMatch(filters, filteredStemdoers);
      isFiltered.value = true;
      isFilteredByClosestMatch.value = true;
      return filteredStemdoers;
    };

    const addStemdoerToCart = (stemdoerId: string) => {
      const stemdoer = stemdoers.value.getById(stemdoerId);
      stemdoersNotInCart.value = visibleStemdoers.value.getFilterById(stemdoerId);
      visibleStemdoers.value = visibleStemdoers.value.getFilterById(stemdoerId);
      store.commit('cartStore/addStemdoer', stemdoer);
      document.documentElement.style.overflow = 'auto';
    };

    const changeAvailableFilter = (field: FilterFields, newVal: boolean) => {
      const payload = { field, newVal };
      store.commit('filterStore/updateSwitchReverseState', payload);
    };

    return {
      stemdoers,
      rates,
      visibleStemdoers,
      FilterFields,
      isFiltered,
      defaultClientSelector,
      defaultCompanySelector,
      isFilteredByClosestMatch,
      displaySkeleton,
      isLoading,
      isManager,
      isStemdoersListEmpty,
      isScrolled,
      isWindowScrolled,
      addStemdoerToCart,
      changeAvailableFilter
    };
  }
});
